import React, { useState } from 'react';
import { X } from 'lucide-react';

/**
 * 奇门发布弹窗组件
 * @param {boolean} isOpen - 是否打开弹窗
 * @param {function} onClose - 关闭弹窗回调
 * @param {function} onSubmit - 提交回调
 * @param {object} chartData - 卦例基本信息
 */
const QimenPublish = ({ isOpen, onClose, onSubmit, chartData }) => {
  const [publishType, setPublishType] = useState('qiuzhu');
  const [qiuzhuForm, setQiuzhuForm] = useState({
    isPaid: false,
    paymentAmount: '',
    endDate: '',
  });
  const [shareForm, setShareForm] = useState({
    isCharged: false,
    chargeAmount: '',
    freeContentRatio: 100,
  });
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const formData = {
        chartId: chartData?.id,
        publishType,
        ...(publishType === 'qiuzhu' ? {
          isPaid: qiuzhuForm.isPaid,
          paymentAmount: qiuzhuForm.isPaid ? parseFloat(qiuzhuForm.paymentAmount) || 0 : 0,
          endDate: qiuzhuForm.endDate || null,
        } : {
          isCharged: shareForm.isCharged,
          chargeAmount: shareForm.isCharged ? parseFloat(shareForm.chargeAmount) || 0 : 0,
          freeContentRatio: parseInt(shareForm.freeContentRatio) || 100,
        }),
      };
      await onSubmit(formData);
      onClose();
    } catch (error) {
      console.error('发布失败:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose} />
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg transform transition-all">
          <div className="flex items-center justify-between p-4 border-b">
            <h3 className="text-lg font-semibold text-gray-900">发布卦例</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
              <X size={20} />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {chartData && (
              <div className="bg-gray-50 rounded-lg p-4 text-sm">
                <div className="font-medium text-gray-700 mb-2">卦例信息</div>
                <div className="grid grid-cols-1 gap-1 text-gray-600">
                  <div>姓名：{chartData.name || '—'}</div>
                  <div>占题：{chartData.content || '—'}</div>
                  <div>占类：{chartData.type || '—'}</div>
                </div>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">发布类型</label>
              <div className="flex gap-4">
                <label className="flex items-center cursor-pointer">
                  <input type="radio" name="publishType" value="qiuzhu" checked={publishType === 'qiuzhu'} onChange={(e) => setPublishType(e.target.value)} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300" />
                  <span className="ml-2 text-sm text-gray-700">求助</span>
                  <span className="ml-1 text-xs text-gray-400">（寻求他人帮助解卦）</span>
                </label>
                <label className="flex items-center cursor-pointer">
                  <input type="radio" name="publishType" value="share" checked={publishType === 'share'} onChange={(e) => setPublishType(e.target.value)} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300" />
                  <span className="ml-2 text-sm text-gray-700">分享</span>
                  <span className="ml-1 text-xs text-gray-400">（分享自己的卦例）</span>
                </label>
              </div>
            </div>
            {publishType === 'qiuzhu' && (
              <div className="space-y-4 p-4 bg-blue-50 rounded-lg">
                <div className="text-sm font-medium text-blue-800 mb-2">求助设置</div>
                <div className="flex items-center">
                  <input type="checkbox" id="isPaid" checked={qiuzhuForm.isPaid} onChange={(e) => setQiuzhuForm({ ...qiuzhuForm, isPaid: e.target.checked })} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" />
                  <label htmlFor="isPaid" className="ml-2 text-sm text-gray-700">设置悬赏积分</label>
                </div>
                {qiuzhuForm.isPaid && (
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">悬赏积分（分）</label>
                    <input type="number" min="0" step="0.01" value={qiuzhuForm.paymentAmount} onChange={(e) => setQiuzhuForm({ ...qiuzhuForm, paymentAmount: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 text-sm" placeholder="请输入悬赏积分" />
                  </div>
                )}
                <div>
                  <label className="block text-sm text-gray-600 mb-1">求助截止日期（可选）</label>
                  <input type="date" value={qiuzhuForm.endDate} onChange={(e) => setQiuzhuForm({ ...qiuzhuForm, endDate: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 text-sm" />
                </div>
              </div>
            )}
            {publishType === 'share' && (
              <div className="space-y-4 p-4 bg-green-50 rounded-lg">
                <div className="text-sm font-medium text-green-800 mb-2">分享设置</div>
                <div className="flex items-center">
                  <input type="checkbox" id="isCharged" checked={shareForm.isCharged} onChange={(e) => setShareForm({ ...shareForm, isCharged: e.target.checked })} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" />
                  <label htmlFor="isCharged" className="ml-2 text-sm text-gray-700">设置为付费内容</label>
                </div>
                {shareForm.isCharged && (
                  <>
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">收费积分（分）</label>
                      <input type="number" min="0" step="0.01" value={shareForm.chargeAmount} onChange={(e) => setShareForm({ ...shareForm, chargeAmount: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 text-sm" placeholder="请输入收费积分" />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">免费浏览内容占比：{shareForm.freeContentRatio}%</label>
                      <input type="range" min="0" max="100" step="10" value={shareForm.freeContentRatio} onChange={(e) => setShareForm({ ...shareForm, freeContentRatio: e.target.value })} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer" />
                      <div className="flex justify-between text-xs text-gray-400 mt-1">
                        <span>全部付费</span>
                        <span>全部免费</span>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">取消</button>
              <button type="submit" disabled={submitting} className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed">{submitting ? '发布中...' : '确认发布'}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default QimenPublish;
